<template>
  <div class="player" :class="{'player--loading' : loading}">
    <div class="player__body">
      <div class="player__content content-container">
        <div class="player__top">
          <div class="player__revert" data-aos="fade" data-aos-delay="100">
            <ButtonRound :data="revertData" @onClick="revert"/>
          </div>

          <button
              type="button" class="player__logo"
              @click="revert"
              data-aos="fade" data-aos-delay="100">
            <Icon name="logo/logo"/>
          </button>

          <div class="player__heading">
            <p class="player__title h2" v-if="title" data-aos="fade" data-aos-delay="125">
              {{ title }}
            </p>
            <p class="player__subtitle" v-if="subtitle" data-aos="fade" data-aos-delay="150">
              {{ subtitle }}
            </p>
          </div>
        </div>

        <div class="player__center" data-aos="fade" data-aos-delay="150">
          <div class="player__spinner" v-show="loading">
            <Spinner/>
          </div>
          <div class="player__toggle" v-show="!loading && toggleData">
            <Toggle v-if="toggleData" :data="toggleData" @onToggle="changeAudioSrc"/>
          </div>
        </div>

        <div class="player__bottom" data-aos="fade" data-aos-delay="150">
          <div class="player__background-toggle">
            <ButtonRound
                :data="backgroundToggle"
                @onClick="togglePicker(true)"/>

            <p v-if="backgroundToggle.text" class="player__background-toggle-text text-tiny">
              {{ backgroundToggle.text }}
            </p>
          </div>

          <div class="player__panel" v-if="!disabledProgram">
            <audio preload="auto" controls crossorigin ref="audio">
              <source :src="audioSrc" type="audio/mp3">
            </audio>
            <div class="player__time">
              <span class="player__current">
                {{ currentTime }}
              </span>
              <span class="player__total">
              {{ totalDuration }}
            </span>
            </div>

            <div class="player__controls">
              <button
                  type="button"
                  class="player__prev"
                  @click="skipBackward"
                  :disabled="disableControls">
                <Icon name="24/rewind-left"/>
                <span class="player__caption text-tiny">
                  15
                </span>
              </button>
              <button
                  type="button"
                  class="player__play"
                  @click="togglePlayback"
                  :disabled="disableControls">
                <Icon v-if="playing" name="32/pause"/>
                <Icon v-else name="32/play"/>
              </button>
              <button
                  type="button"
                  class="player__next"
                  @click="skipForward"
                  :disabled="disableControls">
                <Icon name="24/rewind-right"/>
                <span class="player__caption text-tiny">
                  15
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="player__background" v-if="videoData">
      <video loop preload="auto" playsinline autoplay muted ref="video">
        <source :src="videoData.src" :type="videoData.type">
      </video>

      <audio loop preload="auto" autoplay ref="videoAudio" v-if="videoSrc">
        <source :src="videoSrc">
      </audio>
    </div>

    <div class="player__picker" v-if="showPicker">
      <Picker
          @onClose="togglePicker(false)"
          @onReload="onReloadVideo"/>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon/Icon.vue';
import Toggle from '@/components/Toggle/Toggle.vue';
import ButtonRound from '@/components/ButtonRound/ButtonRound.vue';
import Picker from '@/components/Picker/Picker.vue';
import Spinner from '@/components/Spinner/Spinner.vue';
import { mapGetters, mapMutations } from 'vuex';
import Plyr from 'plyr';

export default {
  name: 'Player',
  components: {
    Spinner,
    Picker,
    ButtonRound,
    Toggle,
    Icon,
  },
  data() {
    return {
      // плеер Plyr
      player: null,

      // состояние загрузки аудио
      loading: true,

      // состояние модального окна с выбором фонового видео
      showPicker: false,

      // состояние воспроизведения
      playing: false,

      // воспроизведение медитации закончилось
      end: false,

      // текущее время воспроизведения
      currentTime: '00:00',

      // продолжительность медитации
      totalDuration: '00:00',

      // данные кнопки Назад
      revertData: {
        icon: '24/chevron-left',
      },
    };
  },
  computed: {
    ...mapGetters({
      // данные текущей медитации
      currentProgramData: 'app/getCurrentProgramData',

      // id первого видео
      firstVideoId: 'app/getFirstVideoId',

      // src текущей медитации
      audioSrc: 'app/getCurrentAudioSrc',
    }),

    // заголовок
    title() {
      return this.currentProgramData?.name;
    },

    // подзаголовок
    subtitle() {
      return this.$store.getters['app/getStaticValue']('MAIN_TITLE');
    },

    // src и type видео
    videoData() {
      if (!this.$store.getters['app/getCurrentVideo']) return null;

      const src = `${process.env.VUE_APP_API_URL}${this.$store.getters['app/getCurrentVideo']?.src}`;
      const type = this.$store.getters['app/getCurrentVideo']?.type;

      return {
        src,
        type,
      };
    },

    // src видео
    videoSrc() {
      return this.videoData?.src || null;
    },

    // данные кнопки смены видео
    backgroundToggle() {
      return {
        size: 'medium',
        icon: '24/wave',
        text: 'Выбрать фон',
        disabled: this.loading,
      };
    },

    // медитация неактивна
    disabledProgram() {
      return this.currentProgramData?.disabled;
    },

    // данные для переключателя спикеров
    toggleData() {
      // eslint-disable-next-line no-unreachable
      if (!this.currentProgramData?.speakers) return null;

      if (this.disabledProgram) return null;

      return {
        title: this.$store.getters['app/getStaticValue']('SPEAKERS_LIST_DESCRIPTION'),
        items: (this.currentProgramData?.speakers || []).map((item) => ({
          audio: `${process.env.VUE_APP_API_URL}${item?.audio}`,
          id: item.id,
          code: item.code,
          image: item.image,
          name: item.name,
        })),
      };
    },

    // кнопки плеера неактивны
    disableControls() {
      return this.loading;
    },
  },
  methods: {
    ...mapMutations({
      // смена мода (состояния) приложения
      setAppMode: 'app/setMode',

      // сброс src текущей медитации
      resetAudioSrc: 'app/resetAudioSrc',

      // выбор видео по id
      toggleActive: 'app/backgroundListToggleActive',
    }),

    // возврат на главную
    revert() {
      this.setAppMode('body');
    },

    // воспроизвести/поставить на паузу
    togglePlayback() {
      if (!this.player) return;
      if (this.loading) return;

      if (this.playing) {
        this.player.pause();
      } else {
        this.player.play();
      }
    },

    // прокрутка вперед на 15 секунд
    skipForward() {
      if (this.loading || !this.player) return;

      this.player.forward(15);
    },

    // прокрутка назад на 15 секунд
    skipBackward() {
      if (this.loading || !this.player) return;

      this.player.rewind(15);
    },

    convertNumberToTime(number) {
      const minutes = Math.floor(number / 60);
      const seconds = (number % 60).toFixed();

      const formattedMinutes = String(minutes).padStart(2, '0');
      const formattedSeconds = String(seconds).padStart(2, '0');

      return `${formattedMinutes}:${formattedSeconds}`;
    },

    playerDestroy() {
      this.playing = false;
      this.loading = true;
      this.end = false;

      if (this.player) {
        this.player.destroy();
        this.player = null;
      }

      const { audio } = this.$refs;

      if (audio) {
        audio.load();
      }
    },

    // смена диктора
    changeAudioSrc() {
      const { audio } = this.$refs;

      if (this.player && audio) {
        this.$nextTick(() => {
          audio.src = this.audioSrc;
          this.player.currentTime = 0;
          this.player.play();
        });
      }
    },

    // открытие/скрытие модального окна с выбором видео
    togglePicker(value = true) {
      this.showPicker = value;

      // если модальное окно с выбором видео было закрыто после завершения медитации
      if (!value && this.end) {
        this.setAppMode('second-check');
      }
    },

    // обновления видео после изменения источника (src) при смене видео
    onReloadVideo() {
      this.$nextTick(() => {
        const { video, videoAudio } = this.$refs;

        if (video) {
          video.load();
        }

        if (videoAudio) {
          videoAudio.load();
        }
      });
    },

    // инициализация плеера Plyr
    playerInit() {
      this.loading = true;

      const { audio, videoAudio } = this.$refs;

      this.player = new Plyr(audio, {
        hideControls: false,
        autoplay: true,
        playsinline: true,
        volume: 1,
        controls: [
          'progress',
        ],
      });

      this.player.on('loadedmetadata', () => {
        this.loading = false;
        this.totalDuration = this.convertNumberToTime(this.player.duration);
      });

      this.player.on('timeupdate', () => {
        this.currentTime = this.convertNumberToTime(this.player.currentTime);
      });

      this.player.on('play', () => {
        this.playing = true;

        if (videoAudio) {
          videoAudio.play();
        }
      });

      this.player.on('pause', () => {
        this.playing = false;

        if (videoAudio) {
          videoAudio.pause();
        }
      });

      this.player.on('ended', () => {
        this.playing = false;
        this.end = true;

        if (videoAudio) {
          videoAudio.pause();
        }

        // если модальное окно с выбором видео не открыто
        if (!this.showPicker) {
          this.setAppMode('second-check');
        }
      });
    },
  },
  beforeUnmount() {
    this.playerDestroy();
    this.resetAudioSrc();

    // выбор видео по умолчанию, когда закрывается плеер
    // if (this.firstVideoId) {
    //   this.toggleActive(this.firstVideoId);
    // }
  },
  mounted() {
    this.playerInit();
  },
};
</script>

<style lang="scss" scoped>
@import "Player";
</style>
